// apollo.ts
import { ApolloClient, InMemoryCache, ApolloLink, split, Observable } from '@apollo/client/core';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { createUploadLink } from 'apollo-upload-client';
import { provideApolloClient } from '@vue/apollo-composable';
import { LocalStorageKeys } from './constants/LocalStorageKey.constant';
import { onError, type ErrorHandler } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { PusherLink } from '@/graphql/PusherLink';
import { useUserStore } from '@/store/userStore';
import { isApplicantPublicOperation } from '@/constants/applicant-public-operations.constant';
import { isPublicCheckoutOperation } from '@/constants/public-checkout-operations.constant';
import { isAnonymousOperation, isGuardianPublicOperation } from '@/constants/anonymous-operations.constant';
import { isProtectedPath, loginLocation } from '@/utils/authRedirect';

const graphqlUri = import.meta.env.VITE_BACKEND_BASE_URL as string;

// ✅ Authorization Guard
export const authGuardLink = new ApolloLink((operation, forward) => {
  const token = localStorage.getItem(LocalStorageKeys.token);
  // The allow-list lives in anonymous-operations.constant.ts, where a spec
  // checks it against every operation the public views actually use.
  const isLoginMutation = isAnonymousOperation(operation.operationName);

  if (!token && !isLoginMutation) {
    return new Observable((observer) => {
      observer.error(new Error('Unauthenticated'));
    });
  }

  // The applicant status flow must never carry a logged-in identity: the
  // backend tenant-scopes requests by the authenticated user's school, so a
  // staff member or student checking an application status in the same
  // browser gets the public OTP / tracking lookups scoped to the WRONG
  // school — a fresh, correct code then reads "Invalid or expired
  // verification code". Send these operations exactly like an incognito
  // visitor would. Guardian registration / invitation acceptance create a new
  // account and must not inherit another portal's school either.
  if (
    isApplicantPublicOperation(operation.operationName)
    || isPublicCheckoutOperation(operation.operationName)
    || isGuardianPublicOperation(operation.operationName)
  ) {
    operation.setContext({
      // Public checkout belongs to its form, even when this browser also has
      // a staff/student session. Separate its batch and omit session cookies.
      credentials: 'omit',
      headers: { 'Apollo-Require-Preflight': 'true' },
    });
    return forward(operation);
  }

  // Prefer the Pinia user store (in-memory truth that survives any flow that
  // doesn't go through select-school). Fall back to localStorage in case the
  // store isn't hydrated yet on a hard refresh. The previous version only read
  // from localStorage, so any flow that set currentSchool in the store without
  // writing to localStorage (e.g. branding-driven auto-select, deep link) would
  // silently drop this header on the next upload and EnsureSchoolContext would
  // refuse multi-school users with "specify school in X-Current-School-ID".
  let currentSchoolId: string | null = null;
  try {
    // Links run after Pinia is mounted on the app, so useUserStore() is safe here.
    const storeId = useUserStore().currentSchool?.id;
    if (storeId !== undefined && storeId !== null && storeId !== '') {
      currentSchoolId = String(storeId);
    }
  } catch {
    // Pinia not active yet (extremely early boot edge case) — fall through to localStorage.
  }
  if (!currentSchoolId) {
    currentSchoolId = localStorage.getItem(LocalStorageKeys.currentSchoolId);
  }

  operation.setContext({
    headers: {
      authorization: token ? `Bearer ${token} ` : '',
      ...(currentSchoolId ? { 'X-Current-School-ID': currentSchoolId } : {}),
      'Apollo-Require-Preflight': 'true', // Required for some multipart setups
    },
  });

  return forward(operation);
});

let isRedirectingToLogin = false;

/**
 * Send the user to the login page with a reason and the page they were on, so
 * they are told why and land back where they were after signing in. A request
 * with no token at all (e.g. signed out in another tab) used to return early
 * here and leave the page rendering a raw "Unauthenticated" error.
 */
function forceLogout() {
  if (isRedirectingToLogin) return;
  const hadSession = Boolean(localStorage.getItem(LocalStorageKeys.token));
  const { pathname, search, hash } = window.location;
  const onProtectedPage = isProtectedPath(pathname);
  // Public pages (forms, payments, applicant status) have no session to end.
  if (!hadSession && !onProtectedPage) return;

  isRedirectingToLogin = true;
  localStorage.clear();
  sessionStorage.clear();
  window.location.href = loginLocation(
    hadSession ? 'expired' : 'signed_out',
    onProtectedPage ? `${pathname}${search}${hash}` : null,
  );
}

/** Exported for its unit test; `errorLink` below is the only production caller. */
export const handleOperationErrors: ErrorHandler = ({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    const authzErrors: { permission: string; message: string }[] = [];

    graphQLErrors.forEach((graphQLError) => {
      // A proxy or malformed batch entry can yield an error object with no
      // message; crashing here takes the whole error-handling chain with it
      // (Sentry JAVASCRIPT-VUE-3S).
      const message = graphQLError?.message ?? '';
      const path = graphQLError?.path;

      if (message.includes('Unauthenticated')) {
        forceLogout();
        return;
      }

      if (message.includes('unauthorized')) {
        authzErrors.push({
          permission: path?.join('.') ?? 'unknown',
          message: `Access denied: ${message}`,
        });
      }
    });

    if (authzErrors.length > 0) {
      import('./store/permissionDenialStore').then(({ usePermissionDenialStore }) => {
        const store = usePermissionDenialStore();
        store.addDenials(authzErrors);
      });
    }
  }

  if (networkError?.message?.includes('Unauthenticated')) {
    forceLogout();
  }
};

const errorLink = onError(handleOperationErrors);

const permissionDenialLink = new ApolloLink((operation, forward) => forward(operation).map((response) => {
  const denials = response.extensions?.permission_denials;
  if (Array.isArray(denials) && denials.length > 0) {
    import('./store/permissionDenialStore').then(({ usePermissionDenialStore }) => {
      const store = usePermissionDenialStore();
      store.addDenials(denials);
    });
  }
  return response;
}));

const batchLink = new BatchHttpLink({
  uri: graphqlUri,
  batchInterval: 10,
  batchMax: 2,
});

const uploadLink = createUploadLink({
  uri: graphqlUri,
  headers: {
    'Apollo-Require-Preflight': 'true',
  },
});

// Helper to check if the operation involves a file upload
function isUploadOperation(operation: { variables?: Record<string, unknown> }) {
  const { variables } = operation;
  if (!variables) return false;

  const hasFile = (obj: unknown): boolean => {
    if (!obj) return false;
    if (typeof File !== 'undefined' && obj instanceof File) return true;
    if (typeof Blob !== 'undefined' && obj instanceof Blob) return true;
    if (typeof FileList !== 'undefined' && obj instanceof FileList) return true;

    if (Array.isArray(obj)) {
      return obj.some(v => hasFile(v));
    }

    if (typeof obj === 'object') {
      return Object.values(obj).some(v => hasFile(v));
    }

    return false;
  };

  return hasFile(variables);
}

const terminatingLink = split(
  ({ variables }) => isUploadOperation({ variables }),
  uploadLink,
  batchLink,
);

const pusherLink = new PusherLink();

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  pusherLink,
  terminatingLink,
);

const link = ApolloLink.from([errorLink, permissionDenialLink, authGuardLink, splitLink]);

const apolloClient = new ApolloClient({
  link,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          bills: { merge: false },
          students: { merge: false },
          billItemTemplates: { merge: false },
        },
      },
      // A PublicOption's id is only unique WITHIN its data source (programme 1
      // and campus 1 are different rows). Normalizing them by id makes options
      // from different sources overwrite each other in the cache, so picker
      // values resolve to labels from the wrong source. Keep them embedded.
      PublicOption: { keyFields: false },
      // Admission-letter assignment rows share one type for forms AND
      // programmes, and their ids are only unique within each list: form 1
      // and programme 1 normalised to the same entry, so the form row showed
      // the programme's name (and template). Keep them embedded in their list.
      AdmissionLetterAssignmentTarget: { keyFields: false },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-first',
    },
    query: {
      fetchPolicy: 'cache-first',
    },
  },
});

provideApolloClient(apolloClient);

export { apolloClient };
